import { getArchetypes } from "@/features/branding";
import { isPointWithinSystemBounds } from "@/features/geographic_data";
import {
  enumErrorMap,
  keys,
  PortfolioExportRawSchema,
  rawSchemaBase,
  rawSchemaLocation,
} from "@/tools/aggregate/portfolio-export/schema";
import { parse } from "@climaterisk/papaparse";
import { RcFile } from "antd/es/upload";
import { z, type ZodTypeAny } from "zod";
import { outdatedKeys } from "./../schema";

/** Result from the validation of a .csv file intended for PortfolioExport. */
interface ValidationResult {
  /** Outcome of the validation process. */
  status: "success" | "error";
  /**
   * Any errors (in case of "error" status) or warnings (in case of "success"
   * status) generated during validation.
   */
  messages: string[];
}

const testOneOrNoneOf = (values: (string | number | null | undefined)[]) => {
  const valueCount = values.filter((val) => val !== null && val !== undefined);
  return valueCount.length <= 1;
};

/**
 * Ensures that a chosen CSV file is valid for Portfolio Export upload.
 * @param file - Object generated when a file is selected for upload.
 */
export const validateCsvFile = async (
  file: RcFile,
  portfolioCount: { min: number; max: number },
  regionType?: string,
  creatablePortfolioTypes?: string[]
): Promise<ValidationResult> => {
  const archetypes = Object.freeze(getArchetypes(regionType).map(({ name }) => name)) as readonly [string, ...string[]];

  /** Count of all blank IDs. */
  let blankIdCount = 0;
  /** Count of all coordinates which are outside of system bounds. */
  let outOfBoundsCoordinatesCount = 0;
  /** Count of all lines that have been processed successfully. */
  let processedLineCount = 0;
  /** Using a set ensures that no IDs are duplicated. */
  const uniqueIds = new Set<string>();

  return new Promise((resolve) => {
    parse<PortfolioExportRawSchema>(file, {
      dynamicTyping: true,
      header: true,
      skipEmptyLines: true,
      step: ({ data }, parser) => {
        // check to ensure all headers are correct.
        const validKeys: string[] = Object.values(keys);
        const validOutdatedKeys: string[] = Object.values(outdatedKeys);

        const columnHeaders = Object.keys(data);

        if (!columnHeaders.includes(keys.archetype)) {
          resolve({
            status: "error",
            messages: [`Archetype is a required field. Please ensure an archetype column is included in the template.`],
          });
        }

        columnHeaders.forEach((columnHeader) => {
          if (!validKeys.includes(columnHeader) && !validOutdatedKeys.includes(columnHeader)) {
            resolve({
              status: "error",
              messages: [
                `Invalid header '${columnHeader}' detected in CSV. Please ensure all headers match the template.`,
              ],
            });
          }
        });

        let archetypeTest: ZodTypeAny = z.enum(archetypes, { errorMap: enumErrorMap });

        const otherPortfolioTypes = new Set(creatablePortfolioTypes || []);
        otherPortfolioTypes.delete(regionType || "");

        if (otherPortfolioTypes.size > 0) {
          for (const portfolioType of otherPortfolioTypes) {
            const portfolioArchetypeNames = getArchetypes(portfolioType).map(({ name }) => name);
            const portfolioTypeToName = (pType: string) => (pType === "hk" ? "HKSAR" : "Global");
            archetypeTest = archetypeTest.and(
              z.string().refine((val) => !portfolioArchetypeNames.includes(val), {
                message: `Archetype does not exist within ${regionType ? "the " + portfolioTypeToName(regionType) : "this"} portfolio type,
                but does exist within the ${portfolioTypeToName(portfolioType)} portfolio type. Did you mean to select a different CSV file or portfolio type?`,
              })
            );
          }
        }

        // Wrap schema to use specific archetypes available on this instance.
        const rawSchema = rawSchemaBase
          .extend({
            [keys.archetype]: archetypeTest.nullish().optional(),
          })
          .refine((data) => data[keys.archetype] !== null && data[keys.archetype] !== undefined, {
            message: `Please provide a value for required column "archetype".`,
            path: [keys.archetype],
          })
          .refine(
            (data) =>
              data[keys.elevation] === null ||
              data[keys.elevation] === undefined ||
              (typeof data[keys.latitude] === "number" && typeof data[keys.latitude] === "number"),
            { message: "Elevation cannot be specified without Latitude and Longitude" }
          )
          .refine(
            (data) => {
              const heatThresholdReturnPeriod =
                data[keys.heatThresholdReturnPeriod] ?? data[outdatedKeys.heatThresholdReturnPeriod];
              const heatThresholdReturnFrequency = data[keys.heatThresholdReturnFrequency];
              const heatThresholdTemperature =
                data[keys.heatThresholdTemperature] ?? data[outdatedKeys.heatThresholdTemperature];
              return testOneOrNoneOf([
                heatThresholdReturnPeriod,
                heatThresholdReturnFrequency,
                heatThresholdTemperature,
              ]);
            },
            {
              message:
                "Please provide only one of: 'Extreme Heat Failure Threshold Return Period', 'Extreme Heat Failure Threshold Return Frequency', or 'Extreme Heat Failure Threshold Temperature'.",
              path: [
                keys.heatThresholdReturnPeriod,
                outdatedKeys.heatThresholdReturnPeriod,
                keys.heatThresholdReturnFrequency,
                keys.heatThresholdTemperature,
                outdatedKeys.heatThresholdTemperature,
              ],
            }
          )
          .refine(
            (data) => {
              const windThresholdReturnPeriod =
                data[keys.windThresholdReturnPeriod] ?? data[outdatedKeys.windThresholdReturnPeriod];
              const windThresholdReturnFrequency = data[keys.windThresholdReturnFrequency];
              return testOneOrNoneOf([windThresholdReturnPeriod, windThresholdReturnFrequency]);
            },
            {
              message:
                "Please provide only one of: 'Extreme Wind Threshold Return Period' or 'Extreme Wind Threshold Return Frequency'.",
              path: [
                keys.windThresholdReturnPeriod,
                outdatedKeys.windThresholdReturnPeriod,
                keys.windThresholdReturnFrequency,
              ],
            }
          )
          .refine(
            (data) => {
              const tcwThresholdReturnPeriod =
                data[keys.tcwThresholdReturnPeriod] ?? data[outdatedKeys.tcwThresholdReturnPeriod];
              const tcwThresholdReturnFrequency = data[keys.tcwThresholdReturnFrequency];
              const tcwThresholdWindSpeed =
                data[keys.tcwThresholdWindSpeed] ?? data[outdatedKeys.tcwThresholdWindSpeed];
              return testOneOrNoneOf([tcwThresholdReturnPeriod, tcwThresholdReturnFrequency, tcwThresholdWindSpeed]);
            },
            {
              message:
                "Please provide only one of: 'Tropical Cyclone Wind Threshold Return Period', 'Tropical Cyclone Wind Threshold Return Frequency', or 'Tropical Cyclone Wind Threshold Speed'.",
              path: [
                keys.tcwThresholdReturnPeriod,
                outdatedKeys.tcwThresholdReturnPeriod,
                keys.tcwThresholdReturnFrequency,
                keys.tcwThresholdWindSpeed,
                outdatedKeys.tcwThresholdWindSpeed,
              ],
            }
          )
          .and(rawSchemaLocation);

        // Validate data
        const result = rawSchema.safeParse(data);

        const id = data[keys.id];
        if (result.success) {
          processedLineCount++;
          if (id) {
            uniqueIds.add(String(id));
          } else {
            blankIdCount++;
          }

          // Handle out of bounds coordinates.
          // Global portfolios accept coordinates from anywhere in the world.
          if (regionType !== "global" && keys.latitude in result.data && keys.longitude in result.data) {
            const lat = result.data[keys.latitude];
            const lng = result.data[keys.longitude];

            if (
              lat !== null &&
              lat !== undefined &&
              lng !== null &&
              lng !== undefined &&
              !isNaN(lat) &&
              !isNaN(lng) &&
              !isPointWithinSystemBounds({ lat: +lat, lng: +lng })
            ) {
              outOfBoundsCoordinatesCount++;
            }
          }
        } else {
          console.log("FAILED VALIDATION:", id);
          let identity = `Row ${processedLineCount + 2}`;
          if (id) identity += ` (ID: ${id})`;

          resolve({
            status: "error",
            messages: result.error.issues.map(({ message }) => `${identity}: ${message}`),
          });
          parser.abort();
        }
      },
      complete: () => {
        if (outOfBoundsCoordinatesCount > 0) {
          resolve({
            status: "error",
            messages: ["Assets found of out system bounds."],
          });
        }
        if (portfolioCount.min && processedLineCount < portfolioCount.min) {
          resolve({
            status: "error",
            messages: [`Portfolio size must be at least ${portfolioCount.min}.`],
          });
        }
        if (portfolioCount.max && processedLineCount > portfolioCount.max) {
          resolve({
            status: "error",
            messages: [`Portfolio size cannot exceed ${portfolioCount.max}.`],
          });
        }
        if (uniqueIds.size + blankIdCount !== processedLineCount) {
          resolve({
            status: "error",
            messages: ["Some IDs were duplicated!"],
          });
        } else {
          resolve({
            status: "success",
            messages: ["Assets validated"],
          });
        }
      },
      error: (error) => {
        resolve({
          status: "error",
          messages: [error.message],
        });
      },
    });
  });
};
